import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Plus, Trash2, UserPlus, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUSES = [
  { v: "new", l: "New" },
  { v: "contacted", l: "Contacted" },
  { v: "trial_booked", l: "Trial Booked" },
  { v: "quoted", l: "Quoted" },
  { v: "customer", l: "Customer" },
  { v: "lost", l: "Lost" },
];

const STATUS_COLOR = {
  new: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  contacted: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  trial_booked: "bg-purple-500/15 text-purple-700 border-purple-500/30",
  quoted: "bg-cyan-500/15 text-cyan-700 border-cyan-500/30",
  customer: "bg-green-600/15 text-green-700 border-green-600/30",
  lost: "bg-zinc-500/15 text-zinc-600 border-zinc-500/30",
};

const FUNDING = [
  { v: "self", l: "Self-funded" },
  { v: "ndis", l: "NDIS" },
  { v: "dva", l: "DVA" },
  { v: "aged_care", l: "Aged Care" },
  { v: "other", l: "Other" },
];

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export default function CustomersPanel({ token }) {
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const res = await axios.get(`${API}/admin/customers${params}`, {
        headers: { "X-Admin-Token": token },
      });
      setCustomers(res.data);
    } catch {
      toast.error("Could not load customers.");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const onSaved = () => { setEditing(null); setCreating(false); load(); };

  const visible = customers;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="status-filters">
          <FilterPill label="All" active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
          {STATUSES.map((s) => (
            <FilterPill key={s.v} label={s.l} active={statusFilter === s.v} onClick={() => setStatusFilter(s.v)} />
          ))}
        </div>
        <Button
          data-testid="new-customer-btn"
          size="sm"
          onClick={() => setCreating(true)}
          className="rounded-sm uppercase tracking-wider font-bold"
        >
          <Plus className="h-4 w-4 mr-2" /> New customer
        </Button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="bg-muted/60">
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Funding</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-muted-foreground">
                    No customers in this view yet.
                  </td>
                </tr>
              )}
              {visible.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`customer-row-${c.id}`}
                  onClick={() => setEditing(c)}
                  className="border-t border-border hover:bg-muted/40 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3 align-top font-medium">{c.name}</td>
                  <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-primary hover:underline">
                        <Mail className="h-3 w-3" /> {c.email}
                      </a>
                    )}
                    <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground mt-1">
                      <Phone className="h-3 w-3" /> {c.phone}
                    </a>
                  </td>
                  <td className="px-4 py-3 align-top text-muted-foreground">
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-primary/15 text-primary px-2 py-1 rounded-sm">
                      <MapPin className="h-3 w-3" /> {c.state}
                    </span>
                    {c.suburb && <div className="mt-1 text-xs">{c.suburb}</div>}
                  </td>
                  <td className="px-4 py-3 align-top text-muted-foreground capitalize">
                    {c.funding_source ? c.funding_source.replace("_", " ") : "—"}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap text-muted-foreground">
                    {formatDate(c.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CustomerDialog
        open={creating}
        onClose={() => setCreating(false)}
        token={token}
        onSaved={onSaved}
      />
      <CustomerDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        token={token}
        onSaved={onSaved}
        customer={editing}
      />
    </div>
  );
}

function FilterPill({ label, active, onClick }) {
  return (
    <button
      data-testid={`filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClick}
      className={`text-xs uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded-sm border transition-colors ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card text-muted-foreground border-border hover:border-primary hover:text-primary"
      }`}
    >
      {label}
    </button>
  );
}

export function StatusBadge({ status }) {
  const s = STATUSES.find((x) => x.v === status);
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm border ${STATUS_COLOR[status] || ""}`}
    >
      {s?.l || status}
    </span>
  );
}

function CustomerDialog({ open, onClose, token, onSaved, customer, prefill }) {
  const empty = {
    name: "", email: "", phone: "", state: "",
    street_address: "", suburb: "", funding_source: "", notes: "", status: "new",
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (customer) {
      setForm({
        name: customer.name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        state: customer.state || "",
        street_address: customer.street_address || "",
        suburb: customer.suburb || "",
        funding_source: customer.funding_source || "",
        notes: customer.notes || "",
        status: customer.status || "new",
      });
    } else if (prefill) {
      setForm({ ...empty, ...prefill });
    } else {
      setForm(empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, prefill, open]);

  const set = (k) => (val) => setForm((f) => ({ ...f, [k]: val?.target ? val.target.value : val }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.state) {
      toast.error("Name, phone and state are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (!payload.status) payload.status = "new";
      if (customer) {
        await axios.patch(`${API}/admin/customers/${customer.id}`, payload, {
          headers: { "X-Admin-Token": token },
        });
        toast.success("Customer updated.");
      } else {
        if (prefill?.source_lead_id) payload.source_lead_id = prefill.source_lead_id;
        if (prefill?.source_o2_interest_id) payload.source_o2_interest_id = prefill.source_o2_interest_id;
        await axios.post(`${API}/admin/customers`, payload, {
          headers: { "X-Admin-Token": token },
        });
        toast.success("Customer added.");
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : (detail || "Save failed.");
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!customer) return;
    if (!window.confirm(`Delete ${customer.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/customers/${customer.id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Customer deleted.");
      onSaved();
    } catch {
      toast.error("Could not delete customer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="customer-dialog" className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            {customer ? "Edit customer" : "New customer"}
          </DialogTitle>
          <DialogDescription>
            {customer ? `Last updated ${formatDate(customer.updated_at)}` : "Add a contact to the CRM."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name" required>
              <Input data-testid="cust-name" value={form.name} onChange={set("name")} className="rounded-sm" />
            </Field>
            <Field label="Email">
              <Input data-testid="cust-email" type="email" value={form.email} onChange={set("email")} className="rounded-sm" />
            </Field>
            <Field label="Phone" required>
              <Input data-testid="cust-phone" value={form.phone} onChange={set("phone")} className="rounded-sm" />
            </Field>
            <Field label="State" required>
              <Select value={form.state} onValueChange={set("state")}>
                <SelectTrigger data-testid="cust-state" className="rounded-sm">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Street address">
              <Input data-testid="cust-address" value={form.street_address} onChange={set("street_address")} className="rounded-sm" />
            </Field>
            <Field label="Suburb">
              <Input data-testid="cust-suburb" value={form.suburb} onChange={set("suburb")} className="rounded-sm" />
            </Field>
            <Field label="Funding source">
              <Select value={form.funding_source} onValueChange={set("funding_source")}>
                <SelectTrigger data-testid="cust-funding" className="rounded-sm">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {FUNDING.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="cust-status" className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Notes">
            <Textarea
              data-testid="cust-notes"
              value={form.notes}
              onChange={set("notes")}
              rows={5}
              className="rounded-sm resize-none"
              placeholder="Calls, follow-ups, preferences, accessibility needs..."
            />
          </Field>
          <DialogFooter className="gap-2 flex-row sm:justify-between">
            <div>
              {customer && (
                <Button type="button" variant="ghost" onClick={remove} disabled={busy} className="rounded-sm text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
              <Button data-testid="cust-save-btn" type="submit" disabled={busy} className="rounded-sm uppercase tracking-wider font-bold">
                {customer ? <>Save <ArrowRight className="h-4 w-4 ml-2" /></> : <><UserPlus className="h-4 w-4 mr-2" /> Add customer</>}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
        {label}{required && <span className="text-primary"> *</span>}
      </Label>
      {children}
    </div>
  );
}

export { CustomerDialog };

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
